export const STORE_LINK = "https://studio.landingzone.fr/stores";

const PAGE_MARGIN = 56;
const LINE_HEIGHT = 15;

const BRAND = [94, 119, 255];
const INK = [26, 31, 54];
const BODY = [55, 65, 81];
const MUTED = [105, 115, 134];

/**
 * Convertit le HTML sanitisé du vendeur en blocs de texte imprimables.
 *
 * Le PDF ne reproduit pas le gras / italique / souligné en ligne : jsPDF
 * n'écrit qu'une police à la fois, restituer des styles au sein d'un même
 * paragraphe demanderait un moteur de rendu par segments. On conserve en
 * revanche la structure (paragraphes, listes) et l'URL des liens, qui serait
 * autrement perdue à l'impression.
 */
const htmlToBlocks = (html) => {
  if (!html) return [];

  const parsed = new DOMParser().parseFromString(html, "text/html");
  const blocks = [];

  const textOf = (element) => {
    const clone = element.cloneNode(true);
    clone.querySelectorAll("a[href]").forEach((anchor) => {
      const href = anchor.getAttribute("href");
      const label = anchor.textContent.trim();
      // Un lien cliquable n'existe pas dans du texte imprimé : on expose l'URL.
      anchor.textContent = href && href !== label ? `${label} (${href})` : label;
    });
    return clone.textContent.replace(/\s+/g, " ").trim();
  };

  const walk = (node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent.replace(/\s+/g, " ").trim();
        if (text) blocks.push({ text, bullet: null });
        return;
      }

      if (child.nodeType !== Node.ELEMENT_NODE) return;

      const tag = child.tagName.toLowerCase();

      if (tag === "ul" || tag === "ol") {
        Array.from(child.children).forEach((item, index) => {
          const text = textOf(item);
          if (!text) return;
          blocks.push({ text, bullet: tag === "ol" ? `${index + 1}.` : "•" });
        });
        return;
      }

      if (tag === "br") return;

      const text = textOf(child);
      if (text) blocks.push({ text, bullet: null });
    });
  };

  walk(parsed.body);
  return blocks;
};

/**
 * Charge l'icône du parcours et la convertit en data URL exploitable par jsPDF.
 *
 * L'URL vient de S3 : sans en-têtes CORS, le canvas est « taché » et
 * toDataURL lève. On renvoie null dans tous les cas d'échec — l'icône est
 * décorative, elle ne doit jamais empêcher la génération du PDF.
 */
const loadIconDataUrl = (url) =>
  new Promise((resolve) => {
    if (!url) return resolve(null);

    // La page affiche déjà cette image via un <img> sans crossOrigin : la
    // réponse est donc en cache SANS en-tête CORS. Redemandée en mode CORS,
    // le navigateur réutilise cette entrée (304) et bloque, même si le bucket
    // est correctement configuré. Un paramètre unique force une vraie requête.
    // On ne l'ajoute que si l'URL n'a pas déjà de query string, pour ne pas
    // invalider la signature d'une URL S3 pré-signée.
    const requestUrl = url.includes("?") ? url : `${url}?pdfIcon=${Date.now()}`;

    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    const image = new Image();
    image.crossOrigin = "anonymous";

    image.onload = () => {
      try {
        // jsPDF ne sait pas rogner une image à l'insertion : les coins sont
        // arrondis ici, dans le canvas, avant la conversion.
        const side = Math.min(image.naturalWidth, image.naturalHeight);
        const canvas = document.createElement("canvas");
        canvas.width = side;
        canvas.height = side;

        const ctx = canvas.getContext("2d");

        // Fond blanc plutôt que transparent : la page du PDF est blanche, et
        // certains lecteurs rendent mal la couche alpha des PNG.
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, side, side);

        // Tracé manuel plutôt que roundRect(), absent des navigateurs un peu
        // anciens.
        const radius = side * 0.18;
        const roundedPath = () => {
          ctx.beginPath();
          ctx.moveTo(radius, 0);
          ctx.lineTo(side - radius, 0);
          ctx.quadraticCurveTo(side, 0, side, radius);
          ctx.lineTo(side, side - radius);
          ctx.quadraticCurveTo(side, side, side - radius, side);
          ctx.lineTo(radius, side);
          ctx.quadraticCurveTo(0, side, 0, side - radius);
          ctx.lineTo(0, radius);
          ctx.quadraticCurveTo(0, 0, radius, 0);
          ctx.closePath();
        };

        ctx.save();
        roundedPath();
        ctx.clip();
        // Recadrage centré : l'upload impose le carré, mais une image d'une
        // autre origine ne doit pas être étirée.
        ctx.drawImage(
          image,
          (image.naturalWidth - side) / 2,
          (image.naturalHeight - side) / 2,
          side,
          side,
          0,
          0,
          side,
          side
        );
        ctx.restore();

        roundedPath();
        ctx.strokeStyle = "rgba(0, 0, 0, 0.08)";
        ctx.lineWidth = Math.max(1, side * 0.008);
        ctx.stroke();

        finish({
          dataUrl: canvas.toDataURL("image/png"),
          width: side,
          height: side,
        });
      } catch (error) {
        finish(null);
      }
    };
    image.onerror = () => finish(null);

    // Un S3 injoignable ne déclenche pas toujours onerror rapidement : on ne
    // fait pas attendre l'acheteur plus de quelques secondes.
    setTimeout(() => finish(null), 6000);

    image.src = requestUrl;
  });

/**
 * jsPDF pèse ~350 Ko : il est chargé à la demande pour ne pas alourdir le
 * bundle d'une page vue par tous les acheteurs.
 */
export const generateGameCodePdf = async ({
  tourTitle,
  code,
  customText,
  contactEmail,
  iconUrl,
  t,
}) => {
  const [{ jsPDF }, icon] = await Promise.all([
    import("jspdf"),
    loadIconDataUrl(iconUrl),
  ]);
  const doc = new jsPDF({ unit: "pt", format: "a4" });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - PAGE_MARGIN * 2;
  const title = tourTitle || t("payment_success.pdf.default_title");
  let y = 0;

  const setColor = (rgb) => doc.setTextColor(rgb[0], rgb[1], rgb[2]);

  const ensureSpace = (needed) => {
    if (y + needed <= pageHeight - PAGE_MARGIN) return;
    doc.addPage();
    y = PAGE_MARGIN;
  };

  const writeText = (text, options = {}) => {
    const {
      size = 10.5,
      style = "normal",
      color = BODY,
      lineHeight = LINE_HEIGHT,
      spacingAfter = 0,
      width = contentWidth,
      x = PAGE_MARGIN,
    } = options;

    doc.setFont("helvetica", style);
    doc.setFontSize(size);
    setColor(color);

    const lines = doc.splitTextToSize(text, width);
    ensureSpace(lines.length * lineHeight);
    lines.forEach((line) => {
      doc.text(line, x, y);
      y += lineHeight;
    });
    y += spacingAfter;
  };

  // Bandeau de marque pleine largeur : donne au document l'allure d'un billet
  // plutôt que d'une note imprimée.
  doc.setFillColor(BRAND[0], BRAND[1], BRAND[2]);
  doc.rect(0, 0, pageWidth, 10, "F");

  // L'icône est ancrée en haut à droite ; le bloc de titre se rétrécit pour ne
  // pas passer dessous.
  const ICON_SIZE = 76;
  const headerWidth = icon ? contentWidth - ICON_SIZE - 20 : contentWidth;

  if (icon) {
    doc.addImage(
      icon.dataUrl,
      "PNG",
      pageWidth - PAGE_MARGIN - ICON_SIZE,
      62,
      ICON_SIZE,
      ICON_SIZE
    );
  }

  y = 76;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setCharSpace(1.6);
  setColor(BRAND);
  doc.text(t("payment_success.pdf.eyebrow").toUpperCase(), PAGE_MARGIN, y);
  doc.setCharSpace(0);
  y += 26;

  writeText(title, {
    size: 23,
    style: "bold",
    color: INK,
    lineHeight: 27,
    width: headerWidth,
    spacingAfter: 22,
  });

  // Le titre peut être plus court que l'icône : on repart sous le plus bas
  // des deux pour éviter que l'avertissement ne chevauche l'image.
  if (icon) y = Math.max(y, 62 + ICON_SIZE + 22);

  // Avertissement en encadré ambré : c'est la consigne la plus facile à
  // manquer, elle ne doit pas se fondre dans le corps de texte.
  const calloutPad = 14;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  const warningLines = doc.splitTextToSize(
    t("payment_success.pdf.warning"),
    contentWidth - calloutPad * 2
  );
  const warningHeight = warningLines.length * LINE_HEIGHT + calloutPad * 2 - 4;
  ensureSpace(warningHeight + 26);
  doc.setFillColor(255, 251, 235);
  doc.setDrawColor(252, 211, 77);
  doc.roundedRect(PAGE_MARGIN, y, contentWidth, warningHeight, 8, 8, "FD");
  setColor([146, 84, 14]);
  let warningY = y + calloutPad + 4;
  warningLines.forEach((line) => {
    doc.text(line, PAGE_MARGIN + calloutPad, warningY);
    warningY += LINE_HEIGHT;
  });
  y += warningHeight + 26;

  // Encadré du code : c'est l'information que l'acheteur vient chercher.
  const boxHeight = 54;
  ensureSpace(boxHeight + LINE_HEIGHT);
  doc.setFillColor(238, 242, 255);
  doc.setDrawColor(BRAND[0], BRAND[1], BRAND[2]);
  doc.roundedRect(PAGE_MARGIN, y, contentWidth, boxHeight, 8, 8, "FD");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(63, 81, 181);
  doc.text(t("payment_success.pdf.code_label"), PAGE_MARGIN + 16, y + 20);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  setColor(INK);
  doc.text(code, PAGE_MARGIN + 16, y + 44);
  y += boxHeight + 38;

  const writeSectionHeading = (text) => {
    ensureSpace(34);
    // Filet vertical coloré : repère de section sans alourdir la page.
    doc.setFillColor(BRAND[0], BRAND[1], BRAND[2]);
    doc.rect(PAGE_MARGIN, y - 10, 3, 14, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    setColor(INK);
    doc.text(text, PAGE_MARGIN + 12, y);
    y += 24;
  };

  writeSectionHeading(t("payment_success.pdf.how_to"));

  const steps = [
    t("payment_success.pdf.step1", { link: STORE_LINK }),
    t("payment_success.pdf.step2"),
    t("payment_success.pdf.step3"),
    t("payment_success.pdf.step4", { code }),
    t("payment_success.pdf.step5", { tourTitle: title }),
    t("payment_success.pdf.step6"),
  ];

  const STEP_NUMBER_WIDTH = 20;
  steps.forEach((step, index) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10.5);
    const lines = doc.splitTextToSize(
      step,
      contentWidth - 12 - STEP_NUMBER_WIDTH
    );
    ensureSpace(lines.length * LINE_HEIGHT + 8);

    // Indentation pendante : les lignes qui débordent s'alignent sous le texte
    // et non sous le numéro.
    doc.setFont("helvetica", "bold");
    setColor(BRAND);
    doc.text(`${index + 1}.`, PAGE_MARGIN + 12, y);

    doc.setFont("helvetica", "normal");
    setColor(BODY);
    lines.forEach((line) => {
      doc.text(line, PAGE_MARGIN + 12 + STEP_NUMBER_WIDTH, y);
      y += LINE_HEIGHT;
    });
    y += 8;
  });

  const customBlocks = htmlToBlocks(customText);
  if (customBlocks.length) {
    // Pas de titre de section : le message du vendeur se lit comme la suite
    // naturelle des instructions, un simple filet suffit à le détacher.
    y += 20;
    ensureSpace(LINE_HEIGHT);
    doc.setDrawColor(229, 231, 235);
    doc.line(PAGE_MARGIN, y, pageWidth - PAGE_MARGIN, y);
    y += 24;

    customBlocks.forEach((block) => {
      if (block.bullet) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10.5);
        const lines = doc.splitTextToSize(block.text, contentWidth - 12 - 16);
        ensureSpace(lines.length * LINE_HEIGHT + 6);
        setColor(BRAND);
        doc.text(block.bullet, PAGE_MARGIN + 12, y);
        setColor(BODY);
        lines.forEach((line) => {
          doc.text(line, PAGE_MARGIN + 12 + 16, y);
          y += LINE_HEIGHT;
        });
        y += 6;
        return;
      }

      writeText(block.text, { spacingAfter: 8 });
    });
  }

  if (contactEmail) {
    y += 20;
    const footerPad = 12;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    const footerLines = doc.splitTextToSize(
      t("payment_success.pdf.contact", { email: contactEmail }),
      contentWidth - footerPad * 2
    );
    const footerHeight = footerLines.length * 13 + footerPad * 2 - 3;
    ensureSpace(footerHeight);
    doc.setFillColor(248, 249, 250);
    doc.setDrawColor(229, 231, 235);
    doc.roundedRect(PAGE_MARGIN, y, contentWidth, footerHeight, 8, 8, "FD");
    setColor(MUTED);
    let footerY = y + footerPad + 4;
    footerLines.forEach((line) => {
      doc.text(line, PAGE_MARGIN + footerPad, footerY);
      footerY += 13;
    });
    y += footerHeight;
  }

  doc.save(`landing-zone-${code}.pdf`);
};

export default generateGameCodePdf;
